import FJTextStyled from './FJTextStyled';
import $ from 'jquery';
import FJUtil from '../util/FJUtil';
import FJArchiveUtil from '../util/FJArchiveUtil';
import FJMathUtil from '../util/FJMathUtil';
import FJTextAnimation, { ANIMATION_NAME as TEXT_ANIMATION_NAME } from './motion/FJTextAnimation';
import FJTextAnimationPlayer from './motion/FJTextAnimationPlayer';
import FJStringUtil from '../util/FJStringUtil';
import FJCoreConfig from './FJCoreConfig';

// 1. 字幕不需要创建动画，所有过滤掉相关属性
// 2. 新增了 subtitleTextDelay 和 subtitleTextDuration 用于时间的处理
const INVALID_KEYS = ['delay', 'duration', 'endDelay', 'enterAnimationTime', 'exitAnimationTime'];

class FJSubtitleText extends FJTextStyled {
    constructor(props = {}) {
        super({
            ...props,
            keepAspectRatio: false,
            keepAspectVertical: true,
            keepAspectHorizontal: false,
            isRichTextMode: false,
            textCombinationType: 'word',
        });
        this._type = 'subtitleText';
        this._subtitleTextDelay = FJUtil.isUndefined(props.subtitleTextDelay) ? 0 : props.subtitleTextDelay;
        this._subtitleTextDuration = FJUtil.isUndefined(props.subtitleTextDuration)
            ? FJCoreConfig.defaultSceneDuration
            : props.subtitleTextDuration;
        this._bind = props.bind || false;

        this._wordTimestampList = []; // 字幕时间戳列表
    }

    encode() {
        const data = {
            ...super.encode(),
            subtitleTextDelay: this.getSubtitleTextDelay(),
            subtitleTextDuration: this.getSubtitleTextDuration(),
            bind: this.getBind(),
            wordTimestampList: this.wordTimestampList ? [...this.wordTimestampList] : [],
        };
        INVALID_KEYS.forEach(key => {
            delete data[key];
        });
        return data;
    }

    async decode(data) {
        this._subtitleTextDelay = data.subtitleTextDelay;
        this._subtitleTextDuration = data.subtitleTextDuration;
        this._bind = data.bind;
        this.wordTimestampList = data.wordTimestampList;
        await super.decode(data);
    }

    async decodeRely(data) {
        super.decodeRely(data);
        this._subtitleTextDelay = data.subtitleTextDelay;
        this._subtitleTextDuration = data.subtitleTextDuration;
        this.wordTimestampList = data.wordTimestampList;
        this._bind = data.bind;
    }

    toJSON() {
        const data = {
            ...super.toJSON(),
            subtitleTextDelay: this.getSubtitleTextDelay(),
            subtitleTextDuration: this.getSubtitleTextDuration(),
            bind: this.getBind(),
            wordTimestampList: this.wordTimestampList ? [...this.wordTimestampList] : [],
        };
        INVALID_KEYS.forEach(key => {
            delete data[key];
        });
        return data;
    }

    fromJSON(data) {
        super.fromJSON(data);
        this._subtitleTextDelay = data.subtitleTextDelay;
        this._subtitleTextDuration = data.subtitleTextDuration;
        this._bind = data.bind;
        this.wordTimestampList = data.wordTimestampList ? [...data.wordTimestampList] : [];
    }

    /**
     * @description
     * @param object
     */
    clone(object) {
        if (typeof object === 'undefined') object = new FJSubtitleText();
        object._subtitleTextDelay = this._subtitleTextDelay;
        object._subtitleTextDuration = this._subtitleTextDuration;
        object._bind = this._bind;
        object.wordTimestampList = this.wordTimestampList ? [...this.wordTimestampList] : [];
        super.clone(object);
        return object;
    }

    set wordTimestampList(wordTimestampList) {
        this._wordTimestampList = wordTimestampList;
    }

    get wordTimestampList() {
        return this._wordTimestampList;
    }

    getSubtitleTextDelay() {
        return this._subtitleTextDelay;
    }

    setSubtitleTextDelay(value) {
        this._subtitleTextDelay = Math.max(value, 0);
    }

    getSubtitleTextDuration() {
        return this._subtitleTextDuration;
    }

    setSubtitleTextDuration(value) {
        this._subtitleTextDuration = Math.max(value, 0);
    }

    getParentElementSize() {
        const { width, height } = super.getParentElementSize();
        if (!width || !height) {
            return this._getProjectCanvasSize();
        } else {
            return { width, height };
        }
    }

    /**
     * @desc 更新对象框矩形
     * @param viewSize
     * @param {boolean} hasUpdateNode 是否更新node节点
     */
    updateRelativeRect(viewSize = null, hasUpdateNode = false) {
        if (hasUpdateNode) {
            this.updateNode(true, true);
            this.updateView();
        }
        super.updateRelativeRect(viewSize);
    }

    updateStyle(viewScale, viewSize) {
        const domSize = this.getParentElementSize();
        const parentSize =
            viewSize && viewSize.width > 0 && viewSize.height > 0 ? viewSize : domSize;
        const _viewScale = viewScale || this.getViewScale();
        super.updateStyle(_viewScale, viewSize);

        // SVG 条带背景会超出文字框：放开 fl-text-box；无背景样式（hollow/slant/bright 等）保持 hidden
        const textBoxOverflow = this.background?.enabled ? 'visible' : 'hidden';
        $(this._layer).css({ overflow: textBoxOverflow });
        if (this.background?.enabled) {
            $(this._animationLayer).css({ overflow: 'visible' });
        }

        const relativeRect = this.getRelativeRect();
        if (relativeRect) {
            const rect = FJMathUtil.relativeRectToRect(relativeRect, parentSize);
            $(this._layer).css({
                width: rect.width / _viewScale,
                whiteSpace: 'pre-wrap',
            });
        }
    }

    drawTrack(view) {
        // 时长为 0 时不绘制 track
        if (this.getSubtitleTextDuration() > 0) {
            super.drawTrack(view);
        }
    }

    /**
     * @desc 准备输出到视图
     * @param exportView
     * @param size
     */
    prepareRenderToView(exportView, size) {
        if (this._isExport) return;

        if (!this._exportView) {
            this._exportView = FJArchiveUtil.createExportViewWithSize(size);
        }
        exportView.appendChild(this._exportView);
        $(this._exportView).empty();

        // 将文字对象添加到输出视图中
        this.addToView(this._exportView, size);

        // 输出时先设置 visibility
        this.visibility(true);
        $(this._exportView).hide();
    }

    /**
     * @desc 开始输出帧
     */
    startExportFrames() {
        if (!this._isExport) {
            this._isExport = true;

            if (this._exportView) $(this._exportView).show();
        }
    }

    /**
     * @desc 结束输出帧
     */
    stopExportFrames() {
        if (this._isExport) {
            this._isExport = false;
            this._clearExportView();
        }
    }
    /**
     * @description 设置绑定信息
     * @param {boolean} value
     */
    setBind(value) {
        this._bind = value;
    }
    /**
     * @description 获取绑定信息
     */
    getBind() {
        return this._bind;
    }

    getTimeData() {
        return {
            duration: this.getSubtitleTextDuration(),
            delay: 0,
            endDelay: 0,
        };
    }

    getDetailTime() {
        return {
            showDuration: this.getSubtitleTextDuration(),
            staticDuration: 0,
            hideDuration: 0,
        };
    }

    /**
     * @desc 直接跳转到进场动画结束的帧
     */
    goToCoverFrame() {
        if (this._animation) {
            let useTime = 0;
            if (this._animationInName !== 'None') {
                const cover0AnimationList = [
                    'COLOR_CHANGE',
                    'TEXT_SCALE',
                    'BACKGROUND_MOVE_AND_COLOR_CHANGE',
                    'BACKGROUND_MOVE',
                ];
                if (cover0AnimationList.indexOf(this._animationInName) !== -1) {
                    useTime = 0;
                } else {
                    useTime = this.getSubtitleTextDuration();
                    if (this._textAnimationPlayers && this._textAnimationPlayers.length > 0) {
                        if (this._textAnimationPlayers[0] && this._textAnimationPlayers[0]._animation) {
                            const duration = this._textAnimationPlayers[0]._animation.getDuration() / 1000;
                            if (useTime > duration) {
                                useTime = duration;
                            }
                        }
                    }
                }
            }

            super.seek(useTime);
        }
    }

    playShowAnimation(callback) {
        if (this._animation) {
            if (this._animationInName === 'None') {
                if (callback) callback();
            } else {
                this.seek(0);
                let playDuration = this.getSubtitleTextDuration() * 1000;
                if (playDuration !== 0) {
                    this.play(0);
                    this._setStopPlayTimingTrigger(playDuration, callback);
                }
            }
        }
    }

    /**
     * @description 获取字幕的word时长（word 以 空格隔开）
     * @param wordNumber
     * @returns {*[]}
     * @private
     */
    _getUseWordTimestampList(wordNumber) {
        if (this.wordTimestampList && this.wordTimestampList.length === wordNumber) {
            return [...this.wordTimestampList];
        } else {
            // 時間平均分配
            const singleWordDuration = (this._subtitleTextDuration - 0.1) / wordNumber;
            const tempWordTimestampList = [];
            let sumDuration = 0;
            for (let i = 0; i < wordNumber; i++) {
                tempWordTimestampList.push({
                    startTime: sumDuration,
                    duration: singleWordDuration,
                });
                sumDuration = sumDuration + singleWordDuration;
            }

            return tempWordTimestampList;
        }
    }

    /**
     * @desc 创建针对文字或单词的动画
     * @private
     */
    _createTextAnimation() {
        if (!this._layer) return;

        this._destroyTextAnimation();

        let animationInName = TEXT_ANIMATION_NAME[this._animationInName] ? this._animationInName : 'None';
        let animationLoopName = TEXT_ANIMATION_NAME[this._animationLoopName] ? this._animationLoopName : 'None';
        let animationOutName = TEXT_ANIMATION_NAME[this._animationOutName] ? this._animationOutName : 'None';
        if (animationInName === 'None' && animationOutName === 'None' && animationLoopName === 'None') return;
        const { showDuration, staticDuration, hideDuration } = this.getDetailTime();
        const { duration, delay, endDelay } = this.getTimeData();
        const checkIsContainArabic = FJStringUtil.onlyContainsArabic(this._textData.join(','));

        let __configPlayer = (player, targets) => {
            // 优化阿拉伯语显示及动画
            let tempList = [];
            let rowList = [];
            let initParentNode = null;

            targets.forEach(item => {
                const curParentNode = item.parentNode;
                if (initParentNode !== curParentNode) {
                    initParentNode = curParentNode;
                    if (rowList.length > 0) {
                        tempList = tempList.concat(rowList.reverse());
                        rowList = [];
                        rowList.push(item);
                    } else {
                        rowList.push(item);
                    }
                } else {
                    rowList.push(item);
                }
            });
            tempList = tempList.concat(rowList.reverse());

            player.createAnimation(
                checkIsContainArabic ? tempList : targets,
                animationInName,
                animationLoopName,
                animationOutName,
                {
                    showDuration: showDuration * 1000,
                    hideDuration: hideDuration * 1000,
                    staticDuration: staticDuration * 1000,
                    delay: delay * 1000,
                    duration: duration * 1000,
                    endDelay: endDelay * 1000,
                },
                {
                    speedIn: this._textAnimationInSpeed,
                    speedLoop: this._textAnimationLoopSpeed,
                    speedOut: this._textAnimationOutSpeed,
                    textColor: this._fontColor,
                    animationTextColor: this.animationTextColor,
                    animationBackgroundColor: this.animationBackgroundColor,
                    isSubtitleAnimation: true,
                    wordTimestampList: this._getUseWordTimestampList(targets.length),
                    animationTargetContainer: this._animationTargetContainer(),
                    animationTarget: this._animationTarget(),
                },
            );
            player.activate();
        };

        // 获取文字的层数（如果有shadow或其他style则是多层的）
        let targetLayers = this._style.getAllTextLayers();
        // 重新配置textAnimationPlayer,将多余的player冻结
        for (let i = 0; i < this._textAnimationPlayers.length; i++) {
            let player = this._textAnimationPlayers[i];
            player.stop();
            if (targetLayers.length !== 0) {
                let targets = targetLayers.shift();
                __configPlayer(player, targets);
            } else {
                player.freeze();
            }
        }

        targetLayers.forEach(targets => {
            let player = new FJTextAnimationPlayer();
            __configPlayer(player, targets);
            this._textAnimationPlayers.push(player);
        });
    }

    /**
     * @desc 判断元素内容层是否在指定时间静止
     * @return {boolean}
     */
    isContentStaticAtTime() {
        if (this._animationInName === 'None' || !this._animationInName) {
            return true;
        }

        // 文字特殊动画
        const targetLayers = this._style.getAllTextLayers();
        if (targetLayers.length === 0) {
            return true;
        }

        return !FJTextAnimation.isEffectiveAnimation(this._animationInName);
    }

    play(currentTime = 0) {
        if (currentTime === 0) {
            this.initAnimation();
        }
        return super.play(currentTime);
    }
    initAnimation() {
        this._textAnimationPlayers.forEach(player => {
            if (player.isActive()) player.prepareStartAnimation();
        });
    }

    seek(time) {
        let useTime = time;
        // 输出时，如果seek到0，则需要初始化动画
        if (time === 0) {
            this.initAnimation();
        }
        super.seekInternal(useTime);
    }
    // 字幕不需要整体动画Seek
    renderAtTime(time) {}

    opacity() {
        if (typeof arguments[0] == 'undefined') {
            return this._opacity;
        } else {
            this._opacity = arguments[0];
            this.updateStyle();
        }
    }

    updateSubtitleAnimation(viewSize) {
        this.updateNode();
        this.updateStyle();

        // 由于目前style样式的更新 在不限定宽度换行的情况下会导致换行 所以需要同步更新文本框大小
        this.updateRelativeRect(viewSize);
        this.updateView();
    }
    prepareToExport(size) {
        this.initAnimation();
        super.prepareToExport(size);
    }
}

export default FJSubtitleText;
